// 이미지 압축 유틸리티

export interface CompressionOptions {
  maxWidth?: number;      // 최대 너비 (기본값: 1920px)
  maxHeight?: number;     // 최대 높이 (기본값: 1920px)
  quality?: number;       // 압축 품질 0.0 ~ 1.0 (기본값: 0.8)
  outputFormat?: 'image/jpeg' | 'image/png' | 'image/webp'; // 출력 포맷 (기본값: jpeg)
}

/**
 * 이미지 파일을 압축하는 함수
 * @param file - 압축할 이미지 파일
 * @param options - 압축 옵션
 * @returns 압축된 이미지 파일
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<File> => {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.8,
    outputFormat = 'image/jpeg'
  } = options;

  // 이미지 파일이 아니면 원본 반환
  if (!file.type.startsWith('image/')) {
    console.warn('이미지 파일이 아닙니다:', file.type);
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        try {
          // 원본 크기
          let width = img.width;
          let height = img.height;

          console.log(`📸 원본 이미지 크기: ${width}x${height}, 파일 크기: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

          // 비율 유지하면서 최대 크기 제한
          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;

            if (width > height) {
              width = maxWidth;
              height = maxWidth / aspectRatio;
            } else {
              height = maxHeight;
              width = maxHeight * aspectRatio;
            }

            console.log(`🔄 리사이즈: ${width.toFixed(0)}x${height.toFixed(0)}`);
          }

          // Canvas에 이미지 그리기
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context를 가져올 수 없습니다.'));
            return;
          }

          // 이미지 품질 향상을 위한 설정
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // 이미지 그리기
          ctx.drawImage(img, 0, 0, width, height);

          // Blob으로 변환
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('이미지 압축에 실패했습니다.'));
                return;
              }

              // 압축된 파일 생성
              const compressedFile = new File([blob], file.name, {
                type: outputFormat,
                lastModified: Date.now(),
              });

              const compressionRatio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
              console.log(`✅ 압축 완료: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (${compressionRatio}% 감소)`);

              resolve(compressedFile);
            },
            outputFormat,
            quality
          );
        } catch (error) {
          console.error('❌ 이미지 압축 중 오류:', error);
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('이미지를 로드할 수 없습니다.'));
      };
    };

    reader.onerror = () => {
      reject(new Error('파일을 읽을 수 없습니다.'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * 여러 이미지를 한 번에 압축하는 함수
 * @param files - 압축할 이미지 파일 배열
 * @param options - 압축 옵션
 * @returns 압축된 이미지 파일 배열
 */
export const compressImages = async (
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> => {
  console.log(`🚀 ${files.length}개 이미지 압축 시작`);

  const compressedFiles = await Promise.all(
    files.map(file => compressImage(file, options))
  );

  console.log(`✅ ${files.length}개 이미지 압축 완료`);

  return compressedFiles;
};

/**
 * 상품 대표 이미지용 압축 (높은 품질)
 */
export const compressProductMainImage = async (file: File): Promise<File> => {
  return compressImage(file, {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.85,
    outputFormat: 'image/jpeg'
  });
};

/**
 * 상품 상세 이미지용 압축 (중간 품질)
 */
export const compressProductDetailImage = async (file: File): Promise<File> => {
  return compressImage(file, {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.8,
    outputFormat: 'image/jpeg'
  });
};

/**
 * 샵 이미지용 압축 (높은 품질)
 */
export const compressShopImage = async (file: File): Promise<File> => {
  return compressImage(file, {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.85,
    outputFormat: 'image/jpeg'
  });
};

/**
 * 썸네일용 압축 (작은 크기, 낮은 품질)
 */
export const compressThumbnail = async (file: File): Promise<File> => {
  return compressImage(file, {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.7,
    outputFormat: 'image/jpeg'
  });
};
